import { View, TouchableOpacity, Text, StyleSheet } from 'react-native'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import type { ComponentProps } from 'react'
import { Link } from 'expo-router'
import { mesaColorNames, fontFamilies } from '@/src/common/theme'

interface ChecklistItemProps
  extends Pick<ComponentProps<typeof TouchableOpacity>, 'onPress' | 'testID'> {
  title: string
  description: string
  status: 'complete' | 'incomplete'
  children?: React.ReactNode
  linkProps?: ComponentProps<typeof Link>
}

export function ChecklistItem({
  title,
  description,
  status,
  children,
  onPress,
  linkProps,
}: ChecklistItemProps) {
  const touchableOpacity = (
    <TouchableOpacity
      disabled={!onPress && !linkProps?.href}
      onPress={onPress}
      activeOpacity={0.7}
      style={styles.container}
      testID={linkProps?.testID}
    >
      <View style={[styles.statusContainer, styles[`statusContainer_${status}`]]} />
      <View style={styles.textContainer}>
        <View style={styles.mainContent}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.description}>{description}</Text>
          {Boolean(children) && <View style={styles.childrenContainer}>{children}</View>}
        </View>
        {(onPress || linkProps?.href) && (
          <View style={styles.chevronContainer}>
            <ChevronRight />
          </View>
        )}
      </View>
    </TouchableOpacity>
  )

  return linkProps?.href ? (
    <Link asChild {...linkProps}>
      {touchableOpacity}
    </Link>
  ) : (
    touchableOpacity
  )
}

const styles = StyleSheet.create({
  container: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexDirection: 'row',
    gap: 12,
    backgroundColor: 'white',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  statusContainer: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: mesaColorNames['Greyscale/300 #CDD4D5'],
    marginTop: 2,
  },
  statusContainer_complete: {
    backgroundColor: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
  statusContainer_incomplete: {
    backgroundColor: mesaColorNames['Mesa Third #E78A43'],
  },
  textContainer: {
    flex: 1,
    flexDirection: 'row',
    alignSelf: 'baseline',
    justifyContent: 'space-between',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'column',
    gap: 4,
  },
  chevronContainer: {
    marginLeft: 8,
  },
  title: {
    fontFamily: fontFamilies['Inter-Bold'],
    fontSize: 16,
  },
  description: {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 14,
  },
  childrenContainer: {
    marginTop: 8,
  },
})
